import { useEffect } from "react";
import { ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED } from "@/constants/constants";
import { useVariant } from "@/hooks/useVariant/useVariant";
import {
  ensureIsActive,
  ensureIsActiveVariant,
} from "@/utils/product/ensureIsActive";
import { StandardProps } from "@/components/componentMap";
import { ShipFromStoreUI } from "./ShipFromStoreUI";
import { getDefaultCategory } from "@/utils/product/getDefaultCategory";
import { getOwnBrand } from "@/utils/product/getOwnBrand";
import { sanitiseTitle } from "@/components/Title/sanitiseTitle";
import { dispatchAnalyticsEvent } from "@/utils";

export function ShipFromStore({
  product,
  flags,
}: Pick<StandardProps, "product" | "flags">) {
  const activeProduct = ensureIsActive(product);
  const { variant, isSelected } = useVariant(product);
  const activeVariant = ensureIsActiveVariant(variant);
  const price = activeVariant?.price?.value;
  const productId = activeProduct?.aliases.pimProductId;
  const colour = variant.differentiators?.colour;
  const size = isSelected ? variant.differentiators?.size : undefined;
  const skuId = activeVariant?.aliases.skuId;
  const ownBrand = getOwnBrand(product);
  const sanitisedTitle = sanitiseTitle(variant?.title, ownBrand);
  const dispatchAdobeAnalyticsAddToBasketSuccess =
    flags.dispatchAdobeAnalyticsAddToBasketSuccess.value === true;

  useEffect(() => {
    if (isShipFromStoreNotSelected()) {
      return;
    }

    dispatchAnalyticsEvent(
      "global.impression",
      {
        type: "pdp",
        subType: "express checkout:ship from store",
        additionalInfo: {},
        properties: {},
        products: [
          {
            brand: product.brand?.name,
            category: getDefaultCategory(product),
            sku: skuId,
            price: price,
            productName: sanitisedTitle,
            productId: productId,
            colour: colour,
            size: size,
          },
        ],
      },
      {},
    );

    function dispatchAddBasketSuccess() {
      if (!dispatchAdobeAnalyticsAddToBasketSuccess) {
        return;
      }
      dispatchAnalyticsEvent(
        "addtobasketsuccess",
        {
          type: "shipfromstore",
          buttonType: "expresscheckout",
          qtyRequested: 1,
          product: {
            itemPrice: price,
            productId: productId,
            colour: colour,
            size: size,
            qty: 1,
            services: [],
            sku: skuId,
          },
        },
        {},
      );
    }

    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      dispatchAddBasketSuccess,
    );

    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        dispatchAddBasketSuccess,
      );
    };
  }, [
    price,
    productId,
    colour,
    size,
    skuId,
    dispatchAdobeAnalyticsAddToBasketSuccess,
  ]);

  function isShipFromStoreNotSelected(): boolean {
    return (
      !activeProduct ||
      !activeVariant ||
      !isSelected ||
      !price ||
      !activeVariant.availability.shipFromStore
    );
  }

  if (isShipFromStoreNotSelected()) {
    return null;
  }

  return price && <ShipFromStoreUI price={price} />;
}
